/**
 * [SUB-PLAN 1R-RENDERGAP STONE 1 — 2026-05-29]
 * [SUB-PLAN 1R-RENDERGAP STONE 3a — 2026-05-27] Earring sibling
 *
 * On-load migration helpers for Proposal B (Rule 31 data-model trace:
 * .claude/findings/2026-05-29-datamodel-equipslot-rendergap-1Rrg-s4.md §(d)).
 *
 * Problem: Ring AND earring items in BASE_ITEMS declare bare `equipSlot` values
 * ('ring', 'earring'). EquipmentPanel renders ring1/ring2 and earring1/earring2
 * slots — there are no bare visual slots. Items equipped via old paths are saved
 * with bare equipSlot and are INVISIBLE in the gear grid.
 *
 * Solution (Proposal B): Keep BASE_ITEMS bare (data unchanged).
 * Fix happens at TWO points:
 *   1. On-load — `migratePairedSlots()` promotes loaded items with bare slot
 *      values to their numbered variants (ring→ring1/ring2, earring→earring1/2).
 *   2. At equip-time — `getEquipSlots` returns the paired array for paired items
 *      so new equips always land in a numbered slot (done in InventoryContext.js).
 *
 * PAIRED_SLOT_MAP: central table mapping bare slot → [slot1, slot2] pair.
 * Adding a new paired slot family only requires one row here.
 *
 * [RULE 22]: This module is imported by BOTH InventoryContext.js (production) AND
 * the test file — no inline replicas.
 *
 * [RULE 11/17]: No hardcoded item IDs here. Detection is purely by `equipSlot`
 * field value ('ring', 'earring') — the property-based approach.
 */

/**
 * Central mapping: bare equipSlot → [numbered-slot-1, ...].
 * Supports N-element families (generalized beyond 2). Adding a new paired family
 * = one new row here; all helpers (expandPairedEquipSlots, migratePairedSlots,
 * isPairedSlot) auto-extend without code changes.
 *
 * Ring / earring = 2 slots (D&D 5e RAW).
 * Ioun = 3 slots (DMG p.176: up to 3 Ioun Stones can orbit simultaneously).
 * [SUB-PLAN 1R-followup / 2026-05-28]: added ioun family.
 *
 * @type {Object.<string, string[]>}
 */
export var PAIRED_SLOT_MAP = {
  ring: ['ring1', 'ring2'],
  earring: ['earring1', 'earring2'],
  // [SUB-PLAN 1R-followup / 2026-05-28]: 3-orbit Ioun Stone family per DMG p.176.
  //   BASE_ITEMS rows declare bare equipSlot:'ioun'. At equip-time this expands to
  //   ioun1/ioun2/ioun3 (first-empty slot wins). On-load migratePairedSlots promotes
  //   legacy saves with bare 'ioun' (equipped) → ioun1.
  //   Naming: matches ring1/ring2, earring1/earring2 (no underscore separator).
  ioun: ['ioun1', 'ioun2', 'ioun3'],
};

/**
 * Given the raw equipSlot string from a BASE_ITEMS row (or saved item), return
 * the array of UI-facing slots that the player can equip to.
 *
 * Generalized: uses PAIRED_SLOT_MAP so ring AND earring (and any future paired
 * slot families) are handled by a single lookup.
 *
 * This is the pure, stateless version of the expansion logic — it does NOT
 * look at current inventory state (no slot-conflict resolution). InventoryContext
 * uses this to build the menu the ContextMenu renders.
 *
 * @param {string|null|undefined} baseEquipSlot — the raw equipSlot value
 * @returns {string[]} — array of UI slot strings
 */
export function expandPairedEquipSlots(baseEquipSlot) {
  if (baseEquipSlot == null) return [];
  var pair = PAIRED_SLOT_MAP[baseEquipSlot];
  if (pair) return pair;
  return [baseEquipSlot];
}

/**
 * Backward-compatible alias: `expandRingEquipSlots` now delegates to
 * `expandPairedEquipSlots`. All existing call sites continue to work without
 * modification. (Ring items, earring items, and all others are handled.)
 *
 * @param {string|null|undefined} baseEquipSlot
 * @returns {string[]}
 */
export function expandRingEquipSlots(baseEquipSlot) {
  return expandPairedEquipSlots(baseEquipSlot);
}

/**
 * Convenience alias for earring items (mirrors expandRingEquipSlots naming pattern).
 * `expandEarringEquipSlots('earring')` → `['earring1', 'earring2']`.
 *
 * @param {string|null|undefined} baseEquipSlot
 * @returns {string[]}
 */
export function expandEarringEquipSlots(baseEquipSlot) {
  return expandPairedEquipSlots(baseEquipSlot);
}

/**
 * On-load migration: walk an inventory array and promote items that were saved
 * with `equipSlot: 'ring'` (equipped state) to `ring1` or `ring2`.
 *
 * Semantics (Proposal B, section (d) of the Rule 31 trace):
 *   - Only promotes items that are currently equipped (equipped === true).
 *   - First equipped ring encountered → ring1
 *   - Second equipped ring encountered → ring2
 *   - Third+ equipped rings: unequip + log warning (D&D 5e RAW allows 2 ring
 *     slots max; a third ring in old save data indicates a save-integrity issue).
 *   - Unequipped ring items with `equipSlot: 'ring'` are left as-is. The
 *     runtime layer (getEquipSlots) will offer ring1/ring2 as equip targets when
 *     the player next equips one.
 *
 * [NOTE]: migrateRingSlots is RING-ONLY. For both ring AND earring migration in
 * a single pass, use `migratePairedSlots` instead.
 *
 * @param {Object[]} inventory — raw inventory array from load/import
 * @returns {Object[]} — new array with ring slots promoted (no mutation)
 */
export function migrateRingSlots(inventory) {
  if (!Array.isArray(inventory)) return inventory;

  let ring1Filled = false;
  let ring2Filled = false;

  return inventory.map((item) => {
    if (!item || item.equipSlot !== 'ring' || !item.equipped) {
      return item;
    }

    // This is an equipped ring with bare 'ring' slot — needs promotion.
    if (!ring1Filled) {
      ring1Filled = true;
      return { ...item, equipSlot: 'ring1' };
    }

    if (!ring2Filled) {
      ring2Filled = true;
      return { ...item, equipSlot: 'ring2' };
    }

    // Third+ equipped ring: unequip with warning.
    // Saves should never have 3+ equipped rings (D&D 5e = 2 ring slots max),
    // but old corrupted data may have it. Unequip gracefully rather than crash.
    if (process.env.NODE_ENV !== 'test') {
      console.warn(
        '[equipSlotMigration] Third+ equipped ring detected on load — unequipping:',
        item.id || item.name
      );
    }
    return { ...item, equipped: false, equipSlot: 'ring' };
  });
}

/**
 * On-load migration: earring sibling to migrateRingSlots.
 * Walk an inventory array and promote items that were saved with bare
 * `equipSlot: 'earring'` (equipped state) to `earring1` or `earring2`.
 *
 * Mirrors ring semantics exactly:
 *   - Only promotes items that are currently equipped (equipped === true).
 *   - First equipped earring → earring1, second → earring2.
 *   - Third+ equipped earrings: unequip gracefully (save-integrity guard).
 *   - Unequipped earring items left as bare 'earring'; runtime expands at equip-time.
 *
 * [SUB-PLAN 1R-RENDERGAP STONE 3a — 2026-05-27]
 *
 * @param {Object[]} inventory — raw inventory array from load/import
 * @returns {Object[]} — new array with earring slots promoted (no mutation)
 */
export function migrateEarringSlots(inventory) {
  if (!Array.isArray(inventory)) return inventory;

  let earring1Filled = false;
  let earring2Filled = false;

  return inventory.map((item) => {
    if (!item || item.equipSlot !== 'earring' || !item.equipped) {
      return item;
    }

    // This is an equipped earring with bare 'earring' slot — needs promotion.
    if (!earring1Filled) {
      earring1Filled = true;
      return { ...item, equipSlot: 'earring1' };
    }

    if (!earring2Filled) {
      earring2Filled = true;
      return { ...item, equipSlot: 'earring2' };
    }

    // Third+ equipped earring: unequip gracefully.
    if (process.env.NODE_ENV !== 'test') {
      console.warn(
        '[equipSlotMigration] Third+ equipped earring detected on load — unequipping:',
        item.id || item.name
      );
    }
    return { ...item, equipped: false, equipSlot: 'earring' };
  });
}

/**
 * Generalized on-load migration: handles ALL paired-slot families in a single
 * pass (currently: ring → ring1/ring2 AND earring → earring1/earring2).
 *
 * Use this in App.jsx load paths instead of calling migrateRingSlots +
 * migrateEarringSlots separately — it is a single array map pass.
 *
 * Algorithm: build one counter per bare slot key from PAIRED_SLOT_MAP, then
 * walk inventory promoting equipped items with bare slots.
 *
 * [SUB-PLAN 1R-RENDERGAP STONE 3a — 2026-05-27]
 *
 * @param {Object[]} inventory — raw inventory array from load/import
 * @returns {Object[]} — new array with all paired slots promoted (no mutation)
 */
export function migratePairedSlots(inventory) {
  if (!Array.isArray(inventory)) return inventory;

  // Build a filled-slot counter for each bare key in PAIRED_SLOT_MAP.
  // e.g. { ring: 0, earring: 0 } — tracks how many slots of each family are filled.
  var filledCounts = {};
  Object.keys(PAIRED_SLOT_MAP).forEach(function(bareSlot) {
    filledCounts[bareSlot] = 0;
  });

  return inventory.map(function(item) {
    if (!item || !item.equipped) return item;

    var pair = PAIRED_SLOT_MAP[item.equipSlot];
    if (!pair) return item; // not a paired slot — pass through

    var filled = filledCounts[item.equipSlot];
    if (filled < pair.length) {
      filledCounts[item.equipSlot] = filled + 1;
      return Object.assign({}, item, { equipSlot: pair[filled] });
    }

    // More equipped items than slots: unequip gracefully.
    if (process.env.NODE_ENV !== 'test') {
      console.warn(
        '[equipSlotMigration] Excess equipped item for slot "' + item.equipSlot +
        '" (slot ' + (filled + 1) + ' of max ' + pair.length + ') — unequipping:',
        item.id || item.name
      );
    }
    return Object.assign({}, item, { equipped: false });
  });
}

// ---------------------------------------------------------------------------
// [SUB-PLAN 1R-eyes-data / 2026-05-27]: SRD lens/goggle items whose canonical
//   equipSlot moved from 'head' to 'eyes' in this arc.
//
//   Migration is BY ID — head-slotted items NOT in this list are untouched
//   (helmets, circlets, etc. stay 'head'). This list is the canonical authority;
//   adding a new lens item requires both a BASE_ITEMS row AND an id here.
//
//   Exported for Rule 22 compliance: tests import this list directly rather
//   than maintaining an inline replica.
// ---------------------------------------------------------------------------

/**
 * IDs of SRD lens/goggle items that moved from equipSlot:'head' → 'eyes'.
 * @type {string[]}
 */
export var LENS_ITEM_IDS = ['goggles_of_night', 'eyes_of_the_eagle', 'eyes_of_minute_seeing'];

/**
 * On-load migration: walk an inventory array and promote lens/goggle items that
 * were saved with `equipSlot: 'head'` (legacy shape) to `equipSlot: 'eyes'`.
 *
 * Semantics:
 *   - Only items whose `id` is in LENS_ITEM_IDS AND whose current `equipSlot`
 *     is exactly `'head'` are migrated. All other items pass through unchanged.
 *   - Idempotent: a second call on already-migrated data is a no-op because
 *     the `item.equipSlot === 'head'` check fails for already-promoted items.
 *   - Does NOT restrict to equipped items — an unequipped goggles item with
 *     the old 'head' value should also be migrated so new equips land in 'eyes'.
 *
 * [SUB-PLAN 1R-eyes-data / 2026-05-27]
 *
 * @param {Object[]} inventory — raw inventory array from load/import
 * @returns {Object[]} — new array with lens item slots promoted (no mutation)
 */
export function migrateLensSlots(inventory) {
  if (!Array.isArray(inventory)) return inventory;
  return inventory.map(function(item) {
    if (item && LENS_ITEM_IDS.indexOf(item.id) !== -1 && item.equipSlot === 'head') {
      return Object.assign({}, item, { equipSlot: 'eyes' });
    }
    return item;
  });
}

// ---------------------------------------------------------------------------

/**
 * Check whether a given equipSlot string is ring-family (bare, ring1, ring2).
 * Used by srdInfusions.js classifier to avoid double-checking names.
 *
 * @param {string|null|undefined} equipSlot
 * @returns {boolean}
 */
export function isRingSlot(equipSlot) {
  return equipSlot === 'ring' || equipSlot === 'ring1' || equipSlot === 'ring2';
}

/**
 * Check whether a given equipSlot string is earring-family (bare, earring1, earring2).
 *
 * [SUB-PLAN 1R-RENDERGAP STONE 3a — 2026-05-27]
 *
 * @param {string|null|undefined} equipSlot
 * @returns {boolean}
 */
export function isEarringSlot(equipSlot) {
  return equipSlot === 'earring' || equipSlot === 'earring1' || equipSlot === 'earring2';
}

/**
 * Check whether a given equipSlot string is ioun-family (bare, ioun1, ioun2, ioun3).
 *
 * [SUB-PLAN 1R-followup / 2026-05-28]: Ioun Stone 3-orbit family per DMG p.176.
 *
 * @param {string|null|undefined} equipSlot
 * @returns {boolean}
 */
export function isIounSlot(equipSlot) {
  return equipSlot === 'ioun' || equipSlot === 'ioun1' || equipSlot === 'ioun2' || equipSlot === 'ioun3';
}

/**
 * Check whether a given equipSlot string belongs to ANY paired-slot family
 * (ring family OR earring family OR any future addition to PAIRED_SLOT_MAP).
 *
 * [SUB-PLAN 1R-RENDERGAP STONE 3a — 2026-05-27]
 *
 * @param {string|null|undefined} equipSlot
 * @returns {boolean}
 */
export function isPairedSlot(equipSlot) {
  if (equipSlot == null) return false;
  // Bare slot key
  if (PAIRED_SLOT_MAP[equipSlot]) return true;
  // Numbered slot (e.g. 'ring1', 'ring2', 'earring1', 'earring2')
  var allPaired = Object.values(PAIRED_SLOT_MAP);
  for (var i = 0; i < allPaired.length; i++) {
    if (allPaired[i].indexOf(equipSlot) !== -1) return true;
  }
  return false;
}
